import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Modal, Popconfirm, message } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import dayjs from "dayjs";
import { ArrowLeft, ExternalLink, FileText, Loader2, Pencil, Trash2, Upload } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";

import { useGetJurisdictionResourcesQuery } from "graphql/queries/get-jurisdiction-resources.generated";
import { useInsertJurisdictionResourcesMutation } from "graphql/mutations/insert-jurisdiction-resources.generated";
import { useUpdateJurisdictionResourceMutation } from "graphql/mutations/update-jurisdiction-resource.generated";
import { useDeleteJurisdictionResourceMutation } from "graphql/mutations/delete-jurisdiction-resource.generated";

import useFileDropzone from "hooks/useFileDropzone";
import { useFileUpload } from "hooks/useFileUpload";
import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { UploadScope } from "state/reactive-vars/uploading-files-var";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";
import routeNames from "constants/route-names";
import { JURISDICTION_RESOURCE_ACCEPTED_TYPES } from "constants/constants";

type Resource = NonNullable<ReturnType<typeof useGetJurisdictionResourcesQuery>["data"]>["jurisdiction_resources"][number];

const PDF_MIME_TYPE = "application/pdf";
const MARKDOWN_MIME_TYPE = "text/markdown";

// The same worker the PDF viewer uses, so a thumbnail does not start a second one.
pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();

const formatBytes = (bytes?: number | null): string => {
	if (!bytes) return "";
	const units = ["B", "KB", "MB", "GB"];
	const i = Math.floor(Math.log(bytes) / Math.log(1024));
	return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const fileKindLabel = (resource: Resource): string => {
	const extension = resource.file_name.split(".").pop()?.toUpperCase();
	return extension && extension.length <= 5 ? extension : "File";
};

// A browser leaves the type of a .md file empty, and the upload rejects a file with no type.
const withMarkdownType = (file: File): File =>
	!file.type && file.name.toLowerCase().endsWith(".md")
		? new File([file], file.name, { type: MARKDOWN_MIME_TYPE, lastModified: file.lastModified })
		: file;

const nameFromFileName = (fileName: string): string => fileName.replace(/\.[^.]+$/, "");

interface ResourceThumbnailProps {
	resource: Resource;
}

// The first page of a PDF; anything else, or a PDF the browser cannot draw, gets an icon.
const ResourceThumbnail = ({ resource }: ResourceThumbnailProps) => {
	const icon = (
		<div className="prres-thumb-icon">
			<FileText size={22} />
			<span>{fileKindLabel(resource)}</span>
		</div>
	);
	if (resource.mime_type !== PDF_MIME_TYPE) return icon;

	return (
		<Document file={resource.file_url} loading={icon} error={icon} noData={icon}>
			<Page pageNumber={1} width={72} renderTextLayer={false} renderAnnotationLayer={false} />
		</Document>
	);
};

/**
 * The jurisdiction's Resources: submittal guidelines, fee schedules and other documents
 * that are neither plan review rules nor adopted codes. Each file is also read by Ollie
 * in customer chats — the database event behind the table keeps the jurisdiction's
 * Doctrine partition in line with this list.
 */
const PlanReviewResources = () => {
	const navigate = useNavigate();
	const { loggedUserValue } = useLoggedUser();
	const { jurisdictionId, picker, emptyReason } = usePlanReviewJurisdiction();
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The resources screen: the submittal guidelines, fee schedules and other documents this authority published outside its plan review rules and adopted codes. Ollie also uses these files to answer customers' questions.",
			jurisdictionId
		}),
		{ jurisdictionId }
	);

	const { canManage, notice } = usePlanReviewSettingsAccess();
	const { uploadFiles, clearAllFiles } = useFileUpload(UploadScope.JURISDICTION_RESOURCES);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const [isUploading, setIsUploading] = useState(false);
	const [editing, setEditing] = useState<Resource | null>(null);
	const [editName, setEditName] = useState("");
	const [editDescription, setEditDescription] = useState("");
	const [replacementFile, setReplacementFile] = useState<{ name: string; url: string; mimeType: string; size: number } | null>(
		null
	);
	const [isReplacing, setIsReplacing] = useState(false);
	const [isSavingEdit, setIsSavingEdit] = useState(false);
	const replaceInputRef = useRef<HTMLInputElement>(null);

	const { data, loading, refetch } = useGetJurisdictionResourcesQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});
	const resources = useMemo(() => data?.jurisdiction_resources ?? [], [data]);

	const [insertResources] = useInsertJurisdictionResourcesMutation();
	const [updateResource] = useUpdateJurisdictionResourceMutation();
	const [deleteResource] = useDeleteJurisdictionResourceMutation();

	// Uploads the picked files to storage and returns them as the row each one becomes.
	const uploadToStorage = useCallback(
		async (files: File[]) => {
			const results = await uploadFiles(files.map(withMarkdownType));
			const sizeByName = new Map(files.map(f => [f.name, f.size]));
			return results
				.filter((r): r is NonNullable<typeof r> => !!r?.publicUrl)
				.map(r => {
					const fileName = r.fileName ?? "document";
					return {
						file_name: fileName,
						file_url: r.publicUrl as string,
						mime_type: r.mimeType || MARKDOWN_MIME_TYPE,
						file_size_bytes: sizeByName.get(fileName) ?? null
					};
				});
		},
		[uploadFiles]
	);

	const handleFilesSelected = useCallback(
		async (fileList: FileList | null) => {
			const files = Array.from(fileList ?? []);
			if (files.length === 0 || !canManage || !jurisdictionId) return;

			setIsUploading(true);
			try {
				const uploaded = await uploadToStorage(files);
				if (uploaded.length > 0) {
					await insertResources({
						variables: {
							objects: uploaded.map(file => ({
								...file,
								jurisdiction_id: jurisdictionId,
								name: nameFromFileName(file.file_name),
								created_by_user_id: loggedUserValue?.id
							}))
						}
					});
					await refetch();
					message.success(uploaded.length === 1 ? "Resource added." : `${uploaded.length} resources added.`);
				}
			} catch (uploadError) {
				console.error("Resource upload failed:", uploadError);
				message.error("Some files couldn't be added. Please try again.");
			} finally {
				// The slot only exists to stage this batch; leaving it full would turn the same
				// file away as "already attached" the next time it is added.
				clearAllFiles();
				setIsUploading(false);
				if (fileInputRef.current) fileInputRef.current.value = "";
			}
		},
		[canManage, jurisdictionId, uploadToStorage, insertResources, loggedUserValue, refetch, clearAllFiles]
	);

	const { isDraggingOver, dropzoneProps } = useFileDropzone(handleFilesSelected, isUploading || !canManage);

	const openEdit = useCallback((resource: Resource) => {
		setEditing(resource);
		setEditName(resource.name);
		setEditDescription(resource.description ?? "");
		setReplacementFile(null);
	}, []);

	const handleReplaceFile = useCallback(
		async (fileList: FileList | null) => {
			const file = fileList?.[0];
			if (!file) return;
			setIsReplacing(true);
			try {
				const [uploaded] = await uploadToStorage([file]);
				if (uploaded) {
					setReplacementFile({
						name: uploaded.file_name,
						url: uploaded.file_url,
						mimeType: uploaded.mime_type,
						size: uploaded.file_size_bytes ?? 0
					});
				}
			} catch (replaceError) {
				console.error("Resource replacement upload failed:", replaceError);
				message.error("The file couldn't be uploaded. Please try again.");
			} finally {
				clearAllFiles();
				setIsReplacing(false);
				if (replaceInputRef.current) replaceInputRef.current.value = "";
			}
		},
		[uploadToStorage, clearAllFiles]
	);

	const handleSaveEdit = useCallback(async () => {
		if (!editing || !editName.trim()) return;
		setIsSavingEdit(true);
		try {
			await updateResource({
				variables: {
					id: editing.id,
					changes: {
						name: editName.trim(),
						description: editDescription.trim() || null,
						...(replacementFile
							? {
									file_name: replacementFile.name,
									file_url: replacementFile.url,
									mime_type: replacementFile.mimeType,
									file_size_bytes: replacementFile.size
								}
							: {})
					}
				}
			});
			await refetch();
			setEditing(null);
			message.success("Resource updated.");
		} catch (saveError) {
			console.error("Resource update failed:", saveError);
			message.error("We couldn't save the resource. Please try again.");
		} finally {
			setIsSavingEdit(false);
		}
	}, [editing, editName, editDescription, replacementFile, updateResource, refetch]);

	const handleDelete = useCallback(
		async (resource: Resource) => {
			try {
				await deleteResource({ variables: { id: resource.id } });
				await refetch();
				message.success("Resource deleted.");
			} catch (deleteError) {
				console.error("Resource delete failed:", deleteError);
				message.error("We couldn't delete the resource. Please try again.");
			}
		},
		[deleteResource, refetch]
	);

	return (
		<>
			<OllieLayout>
				<div className="prres-page">
					<div className="prres-shell">
						<button className="prres-back" onClick={() => navigate(routeNames.PLAN_REVIEW_RULES)}>
							<ArrowLeft size={16} />
							<span>Back to rules</span>
						</button>

						<header className="prres-header">
							<div>
								<h1 className="prres-title">Resources</h1>
								<p className="prres-subtitle">
									Submittal guidelines, fee schedules, and other documentation that falls outside of plan review rules
									and adopted codes. Ollie will also use these files when chatting to help answer customers&apos;
									questions.
								</p>
							</div>
							<div className="prres-header-actions">{picker}</div>
						</header>

						{notice}

						{emptyReason ? (
							<div className="prres-empty">
								<p className="prres-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : (
							<>
								{canManage ? (
									<label className={`prres-dropzone ${isDraggingOver ? "prres-dropzone-active" : ""}`} {...dropzoneProps}>
										<Upload size={20} />
										<span>
											{isUploading
												? "Uploading…"
												: isDraggingOver
													? "Drop the files here"
													: "Drag and drop or tap to upload your resources"}
										</span>
										<span className="prres-meta">PDF, Word, text or Markdown</span>
										<input
											ref={fileInputRef}
											type="file"
											accept={JURISDICTION_RESOURCE_ACCEPTED_TYPES}
											multiple
											className="prres-dropzone-input"
											disabled={isUploading}
											onChange={e => handleFilesSelected(e.target.files)}
										/>
									</label>
								) : null}

								{loading && resources.length === 0 ? (
									<div className="prres-empty">
										<Loader2 className="prres-spinner" />
										<p>Loading resources…</p>
									</div>
								) : resources.length === 0 ? (
									<div className="prres-empty">
										<p className="prres-empty-title">No resources yet</p>
										<p>
											{canManage
												? "Add the documents your applicants and Ollie should be able to refer to."
												: "Your manager hasn't added any resources yet."}
										</p>
									</div>
								) : (
									<div className="prres-list">
										{resources.map(resource => (
											<div key={resource.id} className="prres-card">
												<div className="prres-thumb">
													<ResourceThumbnail resource={resource} />
												</div>
												<div className="prres-card-main">
													<a className="prres-card-name" href={resource.file_url} target="_blank" rel="noreferrer">
														{resource.name}
														<ExternalLink size={13} />
													</a>
													<p className="prres-card-description">
														{resource.description || "No description."}
													</p>
													<p className="prres-meta">
														{[
															resource.file_name,
															formatBytes(resource.file_size_bytes),
															`Added ${dayjs(resource.created_at).format("MMM D, YYYY")}`
														]
															.filter(Boolean)
															.join(" · ")}
													</p>
												</div>
												{canManage ? (
													<div className="prres-card-actions">
														<button
															type="button"
															className="prres-icon-btn"
															title="Edit"
															aria-label="Edit resource"
															onClick={() => openEdit(resource)}
														>
															<Pencil size={16} />
														</button>
														<Popconfirm
															title="Delete this resource?"
															description="Ollie will stop using it in chats. This can't be undone."
															okText="Delete"
															cancelText="Keep it"
															okButtonProps={{ danger: true }}
															onConfirm={() => handleDelete(resource)}
														>
															<button
																type="button"
																className="prres-icon-btn"
																title="Delete"
																aria-label="Delete resource"
															>
																<Trash2 size={16} />
															</button>
														</Popconfirm>
													</div>
												) : null}
											</div>
										))}
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</OllieLayout>

			<Modal
				open={!!editing}
				onCancel={() => setEditing(null)}
				footer={null}
				closable={false}
				width={560}
				className="prres-modal"
			>
				<h2 className="prres-modal-title">Edit resource</h2>
				<div className="prres-form">
					<div className="prres-form-field">
						<label className="prres-label">Name</label>
						<Input value={editName} onChange={e => setEditName(e.target.value)} />
					</div>
					<div className="prres-form-field">
						<label className="prres-label">Description</label>
						<Input.TextArea
							rows={3}
							placeholder="What is this document, and when does it apply?"
							value={editDescription}
							onChange={e => setEditDescription(e.target.value)}
						/>
					</div>
					<div className="prres-form-field">
						<label className="prres-label">File</label>
						<p className="prres-meta">{replacementFile ? `${replacementFile.name} (replaces the current file)` : editing?.file_name}</p>
						<label className="prres-btn">
							{isReplacing ? "Uploading…" : "Replace file"}
							<input
								ref={replaceInputRef}
								type="file"
								accept={JURISDICTION_RESOURCE_ACCEPTED_TYPES}
								className="prres-dropzone-input"
								disabled={isReplacing}
								onChange={e => handleReplaceFile(e.target.files)}
							/>
						</label>
					</div>
				</div>
				<div className="prres-modal-actions">
					<button type="button" className="prres-btn" onClick={() => setEditing(null)} disabled={isSavingEdit}>
						Cancel
					</button>
					<button
						type="button"
						className="prres-btn prres-btn-primary"
						onClick={handleSaveEdit}
						disabled={isSavingEdit || isReplacing || !editName.trim()}
					>
						{isSavingEdit ? "Saving…" : "Save"}
					</button>
				</div>
			</Modal>
		</>
	);
};

export default PlanReviewResources;
